import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { CredentialApprovalRequest } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://0x902r3pk4.execute-api.ap-south-1.amazonaws.com/dev';

// ===== Auth Handlers (configured at app initialization to break circular dependency) =====
let getAuthToken: () => string | null = () => null;
let onUnauthorized: () => void = () => {};

/**
 * Initialize API auth handlers. Call this once after store is created.
 * This breaks the circular dependency between api.ts -> store -> chatSlice -> chatService -> api.ts
 */
export const initializeApiAuth = (
  tokenGetter: () => string | null,
  logoutHandler: () => void
): void => {
  getAuthToken = tokenGetter;
  onUnauthorized = logoutHandler;
};

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAuthToken();
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - trigger logout and redirect
      // But only if there's no fresh token (to avoid redirect loops after login)
      const currentToken = localStorage.getItem('token');
      const requestToken = error.config?.headers?.Authorization?.replace('Bearer ', '');
      
      // Only logout/redirect if the token used in request matches current token
      // If they differ, it means a new login happened and we should not redirect
      if (!currentToken || currentToken === requestToken) {
        onUnauthorized();
        // Hard redirect to login page to ensure clean state
        // Only redirect if not already on login/signup page to avoid loops
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/signup') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ===== Profile Upload APIs =====

export interface PresignedUrlResponse {
  uploadUrl: string;
  fileUrl: string;
  key: string;
}

// Get presigned URL for profile photo upload
export const getProfilePhotoUploadUrl = async (fileName: string, contentType: string): Promise<PresignedUrlResponse> => {
  const response = await api.post('/profile/upload-url/photo', { fileName, contentType });
  return response.data;
};

// Get presigned URL for certification document upload
export const getCertificationUploadUrl = async (fileName: string, contentType: string): Promise<PresignedUrlResponse> => {
  const response = await api.post('/profile/upload-url/certification', { fileName, contentType });
  return response.data;
};

// Upload file to S3 using presigned URL
export const uploadFileToS3 = async (uploadUrl: string, file: File | Blob, contentType?: string): Promise<void> => {
  const fileContentType = contentType || (file instanceof File ? file.type : 'image/jpeg');
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': fileContentType,
    },
  });
};

// Submit certification for approval
export const submitCertification = async (certification: {
  name: string;
  issuingOrganization: string;
  issueDate?: string;
  expiryDate?: string;
  documentUrl: string;
}): Promise<{ certificationId: string; requestId: string; message: string }> => {
  const response = await api.post('/profile/certification/submit', certification);
  return response.data;
};

// Get certification status for current user
export const getCertificationStatus = async (): Promise<CredentialApprovalRequest[]> => {
  const response = await api.get('/profile/certification/status');
  return response.data;
};

// Check if current user is admin
export const checkIsAdmin = async (): Promise<{ isAdmin: boolean }> => {
  const response = await api.get('/profile/is-admin');
  return response.data;
};

// Update user profile type (Individual or Business)
export const updateProfileType = async (profileType: 'INDIVIDUAL' | 'BUSINESS'): Promise<void> => {
  const response = await api.patch('/user/profile/type', { profileType });
  return response.data;
};

// ===== Admin APIs =====

// Get all approval requests (admin only)
export const getApprovalRequests = async (status?: 'pending' | 'approved' | 'rejected'): Promise<CredentialApprovalRequest[]> => {
  const params = status ? { status } : {};
  const response = await api.get('/admin/approvals', { params });
  return response.data;
};

// Get pending approval requests (admin only)
export const getPendingApprovals = async (): Promise<CredentialApprovalRequest[]> => {
  const response = await api.get('/admin/approvals/pending');
  return response.data;
};

// Get approval statistics (admin only)
export const getApprovalStats = async (): Promise<{ total: number; pending: number; approved: number; rejected: number }> => {
  const response = await api.get('/admin/approvals/stats');
  return response.data;
};

// Approve a credential request (admin only)
export const approveCredential = async (requestId: string, adminNotes?: string): Promise<CredentialApprovalRequest> => {
  const response = await api.post(`/admin/approvals/${requestId}/approve`, { adminNotes });
  return response.data;
};

// Reject a credential request (admin only)
export const rejectCredential = async (requestId: string, adminNotes: string): Promise<CredentialApprovalRequest> => {
  const response = await api.post(`/admin/approvals/${requestId}/reject`, { adminNotes });
  return response.data;
};

export default api;
